import { Calendar, Callout, DayOfWeek, DirectionalHint, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import moment from 'moment-timezone';
import * as React from 'react';
import { TimeSlot, TimeSlotData, TimeSlotState } from '../../../api/timeslots.types';
import * as DateUtil from '../../../util/Date';
import './TimeSlots.scss';
import { TimeSlotSelector } from './TimeSlotSelector';

export interface TimeSlotsProps {
  timeslots: TimeSlotState;
  when: TimeSlot | null;
  onChanged: (timeSlot: TimeSlot) => void;
}

export interface TimeSlotsState {
  selectedDate: Date;
  selectedTimeSlot: string;
  selectedTimeSlotId: string;
  showCalendar: boolean;
  dateToSlotsMap: { [date: string]: TimeSlotData[] };
  datesArray: string[];
}

export class TimeSlots extends React.Component<TimeSlotsProps, TimeSlotsState> {
  private calendarButtonElement: HTMLElement | undefined;

  constructor(props: TimeSlotsProps) {
    super(props);
    this.state = {
      selectedDate: new Date(),
      selectedTimeSlotId: '',
      selectedTimeSlot: '',
      showCalendar: false,
      dateToSlotsMap: {},
      datesArray: [],
    };
  }

  public componentDidMount() {
    const { when } = this.props;
    if (when) {
      this.setState({
        selectedDate: new Date(when.date),
        selectedTimeSlotId: when.timeSlotId,
        selectedTimeSlot: when.timeSlot,
      });
    }
  }

  public componentDidUpdate(prevProps: TimeSlotsProps) {
    if (prevProps.timeslots.isFetching && !this.props.timeslots.isFetching && this.props.timeslots.data) {
      this.setToNextAvailableTimeSlot(this.props.timeslots.data);
    }
  }

  public render(): JSX.Element {
    if (this.props.timeslots.error) {
      return <MessageBar messageBarType={MessageBarType.error}>{this.props.timeslots.error}</MessageBar>;
    }
    return (
      <div style={{ marginBottom: 8 }}>
        {this.renderDatePicker()}
        {this.renderTimeslotSelector()}
      </div>
    );
  }

  private renderDatePicker(): JSX.Element {
    const enableRightButton = this.enableRightButton();
    const enableLeftButton = this.enableLeftButton();
    if (this.props.timeslots.isFetching) {
      return <Spinner size={SpinnerSize.large} style={{ padding: 20 }} label="Loading Timeslots..." ariaLive="assertive" />;
    }
    return (
      <div className="date-picker">
        <button
          className="link-button date-picker-left"
          disabled={!enableLeftButton}
          onClick={enableLeftButton ? () => this.nudgeDate(false) : undefined}
          aria-label="Previous day"
          data-tid="btn_datePicker_left"
        >
          <i className="icon ms-Icon ms-Icon--ChevronLeft" />
        </button>
        <button className="link-button date-picker-middle" data-tid="btn_selectedDay" onClick={() => this.setState({ showCalendar: true })}>
          <span ref={(calendarBtn) => (this.calendarButtonElement = calendarBtn!)}>
            <i className="date-icon icon ms-Icon ms-Icon--Calendar" />
            {DateUtil.friendlyFullDate(this.state.selectedDate)}
          </span>
          {this.renderCalendar()}
        </button>
        <button
          className="link-button date-picker-right"
          disabled={!enableRightButton}
          onClick={enableRightButton ? () => this.nudgeDate(true) : undefined}
          aria-label="Next day"
          data-tid="btn_datePicker_right"
        >
          <i className="icon ms-Icon ms-Icon--ChevronRight" />
        </button>
      </div>
    );
  }

  private enableLeftButton(): boolean {
    if (this.props.timeslots.isFetching || !this.props.timeslots.data || this.props.timeslots.data.length === 0) {
      return false;
    }

    const key = moment(this.state.selectedDate).format('MMDDYYYY');
    const index = this.state.datesArray.indexOf(key);
    return index !== -1 && index > 0;
  }

  private enableRightButton(): boolean {
    if (this.props.timeslots.isFetching || !this.props.timeslots.data || this.props.timeslots.data.length === 0) {
      return false;
    }

    const key = moment(this.state.selectedDate).format('MMDDYYYY');
    const index = this.state.datesArray.indexOf(key);
    return index !== -1 && index < this.state.datesArray.length - 1;
  }

  private renderCalendar(): JSX.Element {
    let min;
    let max;
    if (this.state.datesArray.length > 0) {
      min = DateUtil.getZeroTimeDate(new Date(this.state.dateToSlotsMap[this.state.datesArray[0]][0].startTime));
      max = DateUtil.getZeroTimeDate(new Date(this.state.dateToSlotsMap[this.state.datesArray[this.state.datesArray.length - 1]][0].startTime));
    }

    return (
      <>
        {this.state.showCalendar && (
          <Callout
            isBeakVisible={false}
            gapSpace={10}
            doNotLayer={false}
            target={this.calendarButtonElement}
            directionalHint={DirectionalHint.bottomLeftEdge}
            onDismiss={() => this.setState({ showCalendar: false })}
            setInitialFocus={false}
          >
            <Calendar
              onSelectDate={this.onSelectDate}
              onDismiss={() => this.setState({ showCalendar: false })}
              isMonthPickerVisible={true}
              value={this.state.selectedDate!}
              firstDayOfWeek={DayOfWeek.Sunday}
              strings={DateUtil.DatePickerStrings}
              isDayPickerVisible={true}
              highlightCurrentMonth={true}
              minDate={min}
              maxDate={max}
              restrictedDates={this.getRestrictedDates(min, max)}
            />
          </Callout>
        )}
      </>
    );
  }

  private renderTimeslotSelector(): JSX.Element {
    const key = moment(this.state.selectedDate).format('MMDDYYYY');
    const slots = (this.state.dateToSlotsMap[key] || []).map((slot) => ({
      id: slot.id,
      title: slot.longTimeString,
      available: slot.available,
      isDisabled: slot.disabled,
      slotStatus: slot.slotStatus,
      proximal: slot.proximal,
    }));

    // Get the slots for the selectedDate
    const notAvailable = slots.filter((s) => !s.available);
    const delayedBooking = slots.filter((s) => s.slotStatus === 'LOCATION_DELAYED_SERVICE');

    if (slots.length > 0) {
      return (
        <>
          <TimeSlotSelector onSelect={this.onChangeTimeSlot} selectedId={this.state.selectedTimeSlotId} timeslots={slots} />
          {notAvailable.length > 0 && (
            <MessageBar messageBarType={MessageBarType.warning} isMultiline={true}>
              Timeslots in red are full but, if authorized, you can still overbook a full timeslot.
            </MessageBar>
          )}
          {delayedBooking.length > 0 && (
            <MessageBar messageBarType={MessageBarType.warning} isMultiline={true}>
              Timeslots in purple are not available because the visit address is is too far away to be serviced at that time. It is not recommended to choose these slots.
            </MessageBar>
          )}
        </>
      );
    } else {
      return <div className={'no-time-slots'}>{'No time slot available, please try a different date'}</div>;
    }
  }

  private nudgeDate = (nudgeForward: boolean) => {
    const key = moment(this.state.selectedDate).format('MMDDYYYY');
    let index = this.state.datesArray.indexOf(key);
    if (nudgeForward) {
      index++;
    } else {
      index--;
    }

    const slot = this.state.dateToSlotsMap[this.state.datesArray[index]][0];
    this.setState({ selectedDate: DateUtil.getZeroTimeDate(new Date(slot.startTime)) });
  };

  private onSelectDate = (date: Date) => {
    this.setState({
      showCalendar: false,
      selectedDate: date,
      selectedTimeSlotId: '',
      selectedTimeSlot: '',
    });
  };

  private onChangeTimeSlot = (id: string, title: string) => {
    this.setState({ selectedTimeSlotId: id, selectedTimeSlot: title });
    const timeSlot: TimeSlot = {
      date: this.state.selectedDate,
      timeSlotId: id,
      timeSlot: title,
    };
    this.props.onChanged(timeSlot);
  };

  private setToNextAvailableTimeSlot(timeSlotData: TimeSlotData[]): void {
    const dateToSlotsMap: { [date: string]: TimeSlotData[] } = {};
    const datesArray: string[] = [];

    timeSlotData.forEach((timeSlotData) => {
      const key = moment(timeSlotData.startTime).format('MMDDYYYY');
      if (!dateToSlotsMap[key]) {
        dateToSlotsMap[key] = [];
        datesArray.push(key);
      }
      dateToSlotsMap[key].push(timeSlotData);
    });

    this.setState({ dateToSlotsMap, datesArray });

    if (!this.state.selectedTimeSlotId) {
      const nextAvailable = this.getNextAvailableTimeSlot(timeSlotData);
      if (nextAvailable) {
        this.setState({
          selectedDate: DateUtil.getZeroTimeDate(new Date(nextAvailable.startTime)),
          selectedTimeSlotId: '',
          selectedTimeSlot: '',
        });
      }
    }
  }

  private getNextAvailableTimeSlot(timeSlotData: TimeSlotData[]): TimeSlotData | null {
    const today = DateUtil.getZeroTimeDate().getTime();

    for (let i = 0, len = timeSlotData.length; i < len; i++) {
      const timeSlot = timeSlotData[i];
      const timeSlotDate = DateUtil.getZeroTimeDate(new Date(timeSlot.startTime));
      if (timeSlotDate.getTime() >= today) {
        if (timeSlot.available) {
          return timeSlot;
        }
      }
    }

    return null;
  }

  private getRestrictedDates(startDate?: Date, endDate?: Date): Date[] {
    if (!startDate || !endDate) {
      return [];
    }

    const dateArray: Date[] = [];
    let currentDate = startDate;
    while (currentDate <= endDate) {
      const key = moment(currentDate).format('MMDDYYYY');
      if (!this.state.dateToSlotsMap[key]) {
        dateArray.push(new Date(currentDate));
      }
      const date = new Date(currentDate);
      date.setDate(date.getDate() + 1);
      currentDate = date;
    }
    return dateArray;
  }
}
